<template>
  <div class="space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">{{ $t("brand") }}</h1>

    <!-- Actions -->
    <div class="flex gap-3">
      <BaseButton type="primary" @click="fetchBrands">
        {{ $t("refresh") }}
      </BaseButton>

      <BaseButton type="success" @click="openCreateModal">
        + {{ $t("add") }}
      </BaseButton>
    </div>

    <SearchBar v-model="search" @search="fetchBrands" />

    <!-- Table -->
    <BaseDataTable :columns="columns" :data="brands">
      <template #actions="{ row }">
        <TableActions
          :row="row"
          show-edit
          show-delete
          @edit="editBrand(row)"
          @delete="deleteBrand(row.id)"
        />
      </template>
    </BaseDataTable>

    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="fetchBrands"
    />

    <!-- Create / Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded-lg w-[400px] space-y-4">
        <h2 class="text-xl font-bold">
          {{ isEdit ? $t("edit_item") : $t("create_item") }}
        </h2>

        <!-- <input
          v-model="form.name"
          type="text"
          placeholder="Brand name"
          class="w-full border p-2 rounded"
        /> -->
        <div class="space-y-3">
          <div>
            <input
              v-model="form.name_en"
              class="w-full border p-2 rounded focus:ring-1 focus:ring-blue-500 outline-none"
              :placeholder="$t('name_en')"
            />
            <p
              v-if="errors.name_en"
              class="text-red-500 text-[10px] mt-1 font-bold"
            >
              {{ errors.name_en[0] }}
            </p>
          </div>
          <div>
            <input
              v-model="form.name_ar"
              class="w-full border p-2 rounded focus:ring-1 focus:ring-blue-500 outline-none"
              :placeholder="$t('name_ar')"
            />
            <p
              v-if="errors.name_ar"
              class="text-red-500 text-[10px] mt-1 font-bold"
            >
              {{ errors.name_ar[0] }}
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <BaseButton type="secondary" @click="closeModal">{{
            $t("cancel")
          }}</BaseButton>
          <BaseButton type="success" @click="submitBrand">
            {{ isEdit ? $t("update") : $t("create") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import TableActions from "@/components/TableActions.vue";
import brandService from "@/services/brand.service";
import SearchBar from "@/components/SearchBar.vue";
import BasePagination from "@/components/BasePagination.vue";
import { alertSuccess, alertError, alertValidation } from "@/utils/alert";
import Swal from "sweetalert2";

export default {
  name: "BrandListView",
  components: {
    BaseButton,
    BaseDataTable,
    TableActions,
    SearchBar,
    BasePagination,
  },

  data() {
    return {
      brands: [],
      pagination: "",
      search: "",

      showModal: false,
      isEdit: false,
      currentId: null,

      form: {
        name_ar: "",
        name_en: "",
      },
      errors: {},
    };
  },

  computed: {
    columns() {
      return [
        { label: this.$t("id"), field: "id", sortable: true },
        { label: this.$t("name_en"), field: "name_en", sortable: true },
        { label: this.$t("name_ar"), field: "name_ar", sortable: true },
      ];
    },
  },

  methods: {
    /* ================= Fetch ================= */
    async fetchBrands(page = 1) {
      try {
        const res = await brandService.getBrands({
          per_page: 8,
          search: this.search,
          page: page,
        });
        this.brands = res.data.data;
        this.pagination = res.data.pagination;
      } catch (e) {
        alertError(this.$t("failed_load"));
      }
    },

    /* ================= Create ================= */
    openCreateModal() {
      this.isEdit = false;
      this.currentId = null;
      this.form.name_ar = "";
      this.form.name_en = "";
      this.errors = {};
      this.showModal = true;
    },

    async createBrand() {
      try {
        await brandService.createBrand({
          name_ar: this.form.name_ar,
          name_en: this.form.name_en,
        });
        await this.fetchBrands();
        this.closeModal();
        alertSuccess(this.$t("created_success"));
      } catch (e) {
        const bizError = e.response?.data;
        if (bizError) {
          const errorMsg =
            typeof bizError.errors === "string"
              ? bizError.errors
              : typeof bizError.errors === "object"
              ? Object.values(bizError.errors).flat().join("\n")
              : bizError.message;

          alertError(errorMsg || this.$t("failed_create"));

          if (typeof bizError.errors === "object") {
            this.errors = bizError.errors;
          }
        } else {
          alertError(this.$t("failed_create"));
        }
      }
    },

    /* ================= Edit ================= */
    editBrand(brand) {
      this.isEdit = true;
      this.currentId = brand.id;
      this.form.name_en = brand.name_en;
      this.form.name_ar = brand.name_ar;
      this.errors = {};
      this.showModal = true;
    },

    async updateBrand() {
      try {
        await brandService.updateBrand(this.currentId, {
          name_en: this.form.name_en,
          name_ar: this.form.name_ar,
        });
        await this.fetchBrands();
        this.closeModal();
        alertSuccess(this.$t("updated_success"));
      } catch (e) {
        const bizError = e.response?.data;
        if (bizError) {
          const errorMsg =
            typeof bizError.errors === "string"
              ? bizError.errors
              : typeof bizError.errors === "object"
              ? Object.values(bizError.errors).flat().join("\n")
              : bizError.message;

          alertError(errorMsg || this.$t("failed_update"));

          if (typeof bizError.errors === "object") {
            this.errors = bizError.errors;
          }
        } else {
          alertError(this.$t("failed_update"));
        }
      }
    },

    /* ================= Delete ================= */
    async deleteBrand(id) {
      Swal.fire({
        title: this.$t("delete_confirm"),
        text: this.$t("revert_warning"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("yes_delete"),
        cancelButtonText: this.$t("cancel"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await brandService.deleteBrand(id);
            alertSuccess(this.$t("deleted_success"));
            await this.fetchBrands();
          } catch (e) {
            alertError(this.$t("failed_delete"));
          }
        }
      });
    },

    /* ================= Submit ================= */
    async submitBrand() {
      if (this.isEdit) {
        await this.updateBrand();
      } else {
        await this.createBrand();
      }
    },

    closeModal() {
      this.showModal = false;
    },
  },

  mounted() {
    this.fetchBrands(1);
  },
};
</script>
